import React from 'react';
import * as Yup from 'yup';
import { Select } from 'src/design/atoms/Select';
import { ContactDto } from 'src/api/FiretrolApiClient';
import { useContactsByCustomer } from 'src/api/queries/organization/useContactsByCustomer';
import { IContact } from 'src/api/models/Organization';
import { QuickCreateContactModal } from './QuickCreateContactModal';

const toContact = (contact: ContactDto): IContact => ({
  id: contact.id,
  first_name: contact.firstName ?? '',
  last_name: contact.lastName ?? '',
  phone: contact.phone ?? '',
  email: contact.email ?? '',
  customer_number: contact.customerNumber ?? '',
  customer_name: contact.customerName ?? '',
});

const emailSchema = Yup.string().email().required();

// Contact ids are GUIDs, so this sentinel can never collide with a real option value.
const CREATE_OPTION_VALUE = 'contact-picker-create-option';

export interface IContactPickerProps {
  customerNumber: string;
  onChange: (contact: IContact | null) => void;
  initialValue: IContact | null;
  label?: string;
  disabled?: boolean;
  required?: boolean;
  error?: React.ReactNode;
  /** Offers an "add" option for a typed email address; requires a customerNumber. */
  allowCreate?: boolean;
  /** Customer name recorded on a contact created here; the customer may have no contacts yet. */
  customerName?: string;
}

/** @registry Select dropdown of a client's contacts. With allowCreate it can also create one from a typed email address, behind a confirmation dialog. */
export const ContactPicker: React.FC<IContactPickerProps> = ({
  onChange,
  initialValue,
  customerNumber,
  label,
  disabled,
  required,
  error,
  allowCreate = false,
  customerName,
}) => {
  const [search, setSearch] = React.useState('');
  const [pendingEmail, setPendingEmail] = React.useState<string | null>(null);

  const { data: contactDtos, isLoading } =
    useContactsByCustomer(customerNumber);

  const contacts = React.useMemo(
    () => (contactDtos ?? []).map(toContact),
    [contactDtos],
  );
  // An empty customerNumber disables the query, which never leaves the loading state.
  const loading = customerNumber.trim() !== '' && isLoading;

  const source = React.useMemo(
    () =>
      initialValue && !contacts.some((c) => c.id === initialValue.id)
        ? [initialValue, ...contacts]
        : contacts,
    [contacts, initialValue],
  );

  const canCreate = allowCreate && customerNumber !== '';

  const typedEmail = React.useMemo(() => {
    if (!canCreate) return null;
    const trimmed = search.trim();
    if (!emailSchema.isValidSync(trimmed)) return null;
    const lower = trimmed.toLowerCase();
    if (source.some((c) => c.email.toLowerCase() === lower)) return null;
    return trimmed;
  }, [canCreate, search, source]);

  const data = React.useMemo(() => {
    const options = source.map((contact) => ({
      value: contact.id,
      label: `${contact.first_name} ${contact.last_name} (${contact.email})`,
    }));
    return typedEmail
      ? [
          ...options,
          {
            value: CREATE_OPTION_VALUE,
            label: `Add "${typedEmail}" as a new contact`,
          },
        ]
      : options;
  }, [source, typedEmail]);

  const handleChange = (selectedValue: string | null) => {
    if (selectedValue === CREATE_OPTION_VALUE) {
      setPendingEmail(typedEmail);
      return;
    }
    if (selectedValue) {
      const selectedContact =
        source.find((c) => c.id === selectedValue) || null;
      onChange(selectedContact);
    } else {
      onChange(null);
    }
  };

  const handleCreated = (contact: ContactDto) => {
    setPendingEmail(null);
    onChange(toContact(contact));
  };

  return (
    <>
      <Select
        w="100%"
        label={label}
        withAsterisk={required}
        error={error}
        placeholder="Select Contact"
        data={data}
        value={initialValue?.id || null}
        onChange={handleChange}
        onSearchChange={setSearch}
        allowDeselect={false}
        clearable
        searchable
        filter={({ options, search: query }) => {
          const needle = query.trim().toLowerCase();
          return options.filter((option) => {
            if (!('value' in option)) return false;
            if (option.value === CREATE_OPTION_VALUE) return true;
            if (!needle) return true;
            const contact = source.find((c) => c.id === option.value);
            if (!contact) return option.label.toLowerCase().includes(needle);
            return [contact.first_name, contact.last_name, contact.email].some(
              (field) => field.toLowerCase().includes(needle),
            );
          });
        }}
        disabled={disabled || loading}
        nothingFoundMessage={
          loading ? 'Loading contacts...' : 'No contacts found'
        }
      />
      {pendingEmail !== null && (
        <QuickCreateContactModal
          email={pendingEmail}
          customerNumber={customerNumber}
          customerName={customerName ?? ''}
          onCancel={() => setPendingEmail(null)}
          onCreated={handleCreated}
        />
      )}
    </>
  );
};
